import {
  Snackbar,
  Alert,
  // Grid,
  Card,
  Icon,
  CircularProgress,
  Backdrop,
  ListItem,
  // ListItemIcon,
  ListItemText,
  List,
  IconButton,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Divider,
  Box,
} from "@mui/material";

import { useEffect, useRef, useState, useCallback } from "react";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useGeolocated } from "react-geolocated";
import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";
// import AppConfig from "appConfig";
import PushNotificationMgr from "app/components/push_notification_mgr";

function CheckInRequestsPage() {
  const pageTitle = `Check-In Requests`;
  usePageTitle(pageTitle);

  const [checkInRequests, setCheckInRequests] = useState([]);

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [allowPushNotificationDialogState, setAllowPushNotificationDialogState] = useState(false);
  const [openImageCaptureDialog, setOpenImageCaptureDialog] = useState(false);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [imageCapture, setImageCapture] = useState(null);
  const [validatingFace, setValidatingFace] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/campaign/location/checkin/requests.json`)
        .then((result) => {
          setShowLoading(false);
          // console.log(result.data.data);
          if (result.data.status === "success") {
            setCheckInRequests(result.data.data.requests);
          } else {
            setErrMsg(result.data.data.message);
            setSeverity("error");
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setSeverity("error");
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true,
    },
    userDecisionTimeout: 5000,
  });

  function getGPSCoords() {
    if (!isGeolocationAvailable) {
      return false;
    }

    if (!isGeolocationEnabled) {
      return false;
    }

    if (coords) {
      return { latitude: coords.latitude, longitude: coords.longitude };
    }

    return false;
  }

  async function checkIn() {
    if (selectedRequest === null) {
      setSeverity("error");
      setErrMsg("Something went wrong. Refresh this page and try again.");
      setOpen(true);
      return;
    }

    const gpsCoords = getGPSCoords();
    // console.log(gpsCoords);
    if (!gpsCoords) {
      setSeverity("error");
      setErrMsg("Please enable and allow browser's location and GPS access.");
      setOpen(true);
      return;
    }

    if (imageCapture === null) {
      setSeverity("error");
      setErrMsg("Your selfie is required");
      setOpen(true);
      return;
    }

    setOpenImageCaptureDialog(false);
    setShowLoading(true);
    const json = {
      reference: selectedRequest,
      face: imageCapture,
      latitude: gpsCoords.latitude,
      longitude: gpsCoords.longitude,
    };

    // console.log(json);

    await axios
      .post("agents/campaign/location/checkin/request/checkin.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const rnd = (Math.random() + 1).toString(36).substring(9);
  const webcamRef = useRef(rnd); // create a webcam reference

  const [cameraUsed, setCameraUsed] = useState(false);
  const videoConstraints = {
    facingMode: cameraUsed ? "user" : { exact: "environment" },
  };

  async function validateFace(bas64ImageData) {
    await faceapi.loadTinyFaceDetectorModel("/face_models");
    const base64Response = await fetch(bas64ImageData);
    const blob = await base64Response.blob();
    const image = await faceapi.bufferToImage(blob);

    const detection = await faceapi.detectSingleFace(image, new faceapi.TinyFaceDetectorOptions());
    if (!detection) {
      setSeverity("error");
      setErrMsg("Unable to detect your face.");
      setOpen(true);
      return false;
    }

    return true;
  }

  // create a capture function
  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      setSeverity("error");
      setErrMsg("No photo image found.");
      setOpen(true);
      setValidatingFace(false);
      return;
    }

    setValidatingFace(true);
    const passed = await validateFace(imageSrc);
    setValidatingFace(false);
    if (passed) {
      setImageCapture(imageSrc);
    }
  }, [webcamRef]);

  useEffect(() => {
    if (imageCapture === null) {
      return;
    }

    checkIn();
  }, [imageCapture]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PushNotificationMgr
        displayState={allowPushNotificationDialogState}
        setDisplayState={setAllowPushNotificationDialogState}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openImageCaptureDialog}>
        <DialogTitle>
          <MDBox>Selfie</MDBox>
          <MDTypography variant="caption" fontWeight="light">
            Take a selfie at the required location
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>
              <Webcam
                // height={"100%"}
                width="100%"
                ref={webcamRef}
                screenshotQuality={1}
                videoConstraints={videoConstraints}
              />
            </MDBox>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenImageCaptureDialog(false);
            }}
            color="dark"
            iconOnly
            circular
            variant="outlined"
          >
            <Icon>close</Icon>
          </MDButton>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <MDButton
            onClick={() => {
              setCameraUsed(!cameraUsed);
            }}
            color="warning"
            iconOnly
            circular
            variant="outlined"
          >
            <Icon>cameraswitch</Icon>
          </MDButton>
          &nbsp;
          <Box>
            {validatingFace === false ? (
              <MDButton
                color="info"
                variant="outlined"
                iconOnly
                circular
                onClick={() => {
                  // setValidatingFace(true);
                  capture();
                }}
              >
                <Icon>photo_camera</Icon>
              </MDButton>
            ) : (
              <MDBox>
                &nbsp;
                <CircularProgress />
              </MDBox>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <MDBox mb={3}>
        <MDBox>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6">Location Check-In Requests</MDTypography>
              <MDTypography mt={0} variant="body2">
                Enable/Allow browser notifications to stay informed when check-in requests are
                required.
              </MDTypography>
            </MDBox>
          </Card>
        </MDBox>
        <MDBox mt={5}>
          <Card>
            <MDBox p={2}>
              {checkInRequests.length > 0 ? (
                <List>
                  {checkInRequests.map((row) => (
                    <MDBox mb={3}>
                      <ListItem
                        mb={3}
                        onClick={() => {
                          setSelectedRequest(row.reference);
                          setOpenImageCaptureDialog(true);
                          setValidatingFace(false);
                        }}
                      >
                        <ListItemText
                          primary={
                            <MDTypography variant="subtitle2" fontWeight="regular">
                              {row.campaign}
                            </MDTypography>
                          }
                          secondary={row.location}
                        />
                        <MDBox align="center" mb={0}>
                          <MDBox mt={0}>
                            <IconButton color="primary">
                              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                                person_pin
                              </Icon>
                            </IconButton>
                            <MDTypography mt={0} variant="body2">
                              Check-In
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </ListItem>
                      <Divider />
                    </MDBox>
                  ))}
                </List>
              ) : (
                <MDTypography variant="subtitle2">
                  No pending check-in requests at the moment.
                </MDTypography>
              )}
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CheckInRequestsPage;
